import {
  CmdInput,
  CmdLabel,
  CmdLoader,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdTextarea,
} from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import { IOption } from '../../Forms/CmdField';
import FormikValidatorService from '../../../Services/FormikValidatorService';
import { BluecoinRecord } from '../List/BluecoinsListState';
import LoaderService from '../../../Services/LoaderService';
import usePageWithForm from '../../../hooks/usePageWithForm';
import {
  BluecoinAssignment,
  BluecoinAssignmentValidationError,
  UpdateBluecoinAssignment,
} from '../interfaces';
import { transformErrors } from '../../../Services/HookFormService';
import { addAssignment, editAssignment, getAssignment } from '../BluecoinsService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IBluecoinAssignmentFormProps {
  id: number | string | null;
  bluecoinId: number | string;
  closeCallback: React.Dispatch<React.SetStateAction<boolean>>;
  reloadCallback: () => void;
  currencies: IOption[];
}

interface IBluecoinInputForm {
  name: string;
  description: string;
  notes: string;
  unitPrice: number | string;
  unitCurrency: number | string;
}

export default function AssignmentForm(props: IBluecoinAssignmentFormProps): JSX.Element {
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [data, setData] = React.useState<BluecoinAssignment | null>(null);
  const [showLoader, setShowLoader] = React.useState<boolean>(true);
  const [unitCurrencyRequired, setUnitCurrencyRequired] = React.useState<boolean>(false);
  const [currencyId, setCurrencyId] = React.useState<number | string>('');
  const { trackEvent } = useAnalytics();

  const validateFields = (values) => {
    let formErrors = {};
    formErrors = FormikValidatorService.validateByValidationApiResponse(values, {
      name: [
        {
          param: 'minLength',
          value: 1,
          message: f({ id: 'bluecoin.assignmentForm.validation.name.minLength' }),
        },
        {
          param: 'maxLength',
          value: 100,
          message: f({ id: 'bluecoin.assignmentForm.validation.name.maxLength' }),
        },
        {
          param: 'required',
          value: true,
          message: f({ id: 'bluecoin.assignmentForm.validation.name.required' }),
        },
      ],
      description: [
        {
          param: 'minLength',
          value: 1,
          message: f({ id: 'bluecoin.assignmentForm.validation.description.minLength' }),
        },
        {
          param: 'maxLength',
          value: 200,
          message: f({ id: 'bluecoin.assignmentForm.validation.description.maxLength' }),
        },
        {
          param: 'required',
          value: true,
          message: f({ id: 'bluecoin.assignmentForm.validation.description.required' }),
        },
      ],
      unitPrice: [
        {
          param: 'max',
          value: 1000,
          message: `${f({ id: 'form.rules.maxValue' }, { value: 1000 })}.`,
        },
      ],
      notes: [
        {
          param: 'minLength',
          value: 1,
          message: f({ id: 'bluecoin.assignmentForm.validation.notes.minLength' }),
        },
        {
          param: 'maxLength',
          value: 2000,
          message: f({ id: 'bluecoin.assignmentForm.validation.notes.maxLength' }),
        },
        // {
        //   param: 'required',
        //   value: true,
        //   message: f({ id: 'bluecoin.assignmentForm.validation.notes.required' }),
        // },
      ],
      unitCurrency: unitCurrencyRequired
        ? [
            {
              param: 'required',
              value: true,
              message: f({ id: 'bluecoin.assignmentForm.validation.notes.required' }),
            },
          ]
        : [],
    });
    return transformErrors(formErrors);
  };

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    setError,
    trigger,
    formState: { errors },
  } = useForm<IBluecoinInputForm>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      notes: '',
      unitPrice: '',
      unitCurrency: '',
    },
    resolver: async (formData) => {
      const formErrors = validateFields(formData);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const handleFormValues = (assignmentData?: BluecoinAssignment | null) => {
    setValue('name', assignmentData?.deviceName || '');
    setValue('description', assignmentData?.deviceDescription || '');
    setValue('notes', assignmentData?.deviceNotes || '');
    setValue('unitPrice', assignmentData?.unitPrice || '');
    setValue('unitCurrency', assignmentData?.unitCurrency || '');
  };

  const handleAdd = async (values): Promise<boolean> => {
    trackEvent(['bluecoin form', 'click', 'Assign bluecoin']);
    const newValues: UpdateBluecoinAssignment = {
      name: values.name,
      description: values.description,
      notes: values.notes,
      bluecoinId: Number(props.bluecoinId),
      unitCurrency: values.unitCurrency,
      unitPrice: values.unitPrice,
    };
    LoaderService.showLoader(true);
    const response = await addAssignment(newValues);
    if (
      (response as BluecoinAssignmentValidationError).passed === false &&
      (response as BluecoinAssignmentValidationError).errors
    ) {
      Object.keys((response as BluecoinAssignmentValidationError).errors).forEach((key: string) => {
        const formErrors = (response as BluecoinAssignmentValidationError).errors[key];
        formErrors.forEach((error: string) => {
          setError(key, error);
        });
      });
      LoaderService.showLoader(false);
      return false;
    }
    if (response as BluecoinAssignment) {
      handleFormValues();
      setData(null);
      props.reloadCallback();
      LoaderService.showLoader(false);
      props.closeCallback(false);
      return true;
    }
    return false;
  };

  const handleUpdate = async (values): Promise<boolean> => {
    if (props.id) {
      trackEvent(['bluecoin form', 'click', 'Update bluecoin detail']);
      const newValues: UpdateBluecoinAssignment = {
        name: values.name,
        description: values.description,
        notes: values.notes,
        bluecoinId: Number(props.bluecoinId),
        unitCurrency: values.unitCurrency,
        unitPrice: values.unitPrice,
      };
      const response = await editAssignment(Number(props.id), newValues);
      if (
        (response as BluecoinAssignmentValidationError).passed === false &&
        (response as BluecoinAssignmentValidationError).errors
      ) {
        Object.keys((response as BluecoinAssignmentValidationError).errors).forEach(
          (key: string) => {
            const formErrors = (response as BluecoinAssignmentValidationError).errors[key];
            formErrors.forEach((error: string) => {
              setError(key, error);
            });
          }
        );
        return false;
      }
      if (response as BluecoinAssignment) {
        handleFormValues();
        setData(null);
        props.reloadCallback();
        props.closeCallback(false);
        return true;
      }
      return false;
    }
    return false;
  };

  const onSubmit = async (values) => {
    if (props.id) {
      handleUpdate(values);
    } else {
      handleAdd(values);
    }
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'bluecoin.modal.button.close' }),
      closeCallback: props.closeCallback,
    },
  ];

  if (props.id) {
    buttons.push({
      id: 'update-button',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'bluecoin.assignmentForm.button.update' }),
      submitCallback: handleSubmit(onSubmit),
    });
  } else {
    buttons.push({
      id: 'add-button',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'bluecoin.assignmentForm.button.assign' }),
      submitCallback: handleSubmit(onSubmit),
    });
  }

  React.useEffect(() => {
    const getData = async () => {
      if (props.id) {
        const assignmentData = await getAssignment(Number(props.id));
        if (assignmentData) {
          setData(assignmentData);
          handleFormValues(assignmentData);
          setUnitCurrencyRequired(!!assignmentData.unitPrice);
          setCurrencyId(assignmentData.unitCurrency);
        }
      }
      setShowLoader(false);
    };
    getData();
    return () => {
      handleFormValues();
      setData(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUnitCurrencyValidation = (value: number | string) => {
    setValue('unitPrice', value);
    trigger('unitPrice');
    setUnitCurrencyRequired(!!value);
  };

  const getCurrencyName = () => {
    if (!currencyId) {
      return '';
    }
    const currency = props.currencies.find((i) => String(i.value) === String(currencyId));
    return currency?.item;
  };

  return (
    <CmdModal
      id="bluecoin-assignments"
      title={
        props.id
          ? f({ id: 'bluecoin.modal.assignments.titleEdit' })
          : f({ id: 'bluecoin.modal.assignments.title' })
      }
      footerButtons={buttons}
      usePortal={true}
      size="lg"
    >
      {!showLoader && (
        <>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="name"
                type="text"
                label={f({ id: 'bluecoin.assignment.name' })}
                required={true}
                onChangeCallback={(value) => {
                  setValue('name', value);
                  trigger('name');
                }}
                error={errors?.name?.message}
                touched={!!errors?.name?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="description"
                type="text"
                label={f({ id: 'bluecoin.assignment.description' })}
                required={true}
                onChangeCallback={(value) => {
                  setValue('description', value);
                  trigger('description');
                }}
                error={errors?.description?.message}
                touched={!!errors?.description?.message}
              />
            )}
          />
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <CmdTextarea
                {...field}
                id="notes"
                label={f({ id: 'bluecoin.assignment.notes' })}
                setValue={(value) => {
                  setValue('notes', value);
                  trigger('notes');
                }}
                error={errors?.notes?.message}
                touched={!!errors?.notes?.message}
              />
            )}
          />
          <Controller
            name="unitPrice"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                id="notes"
                label={f({ id: 'bluecoin.assignment.unitPrice' })}
                type="number"
                onChangeCallback={handleUnitCurrencyValidation}
                error={errors?.unitPrice?.message}
                touched={!!errors?.unitPrice?.message}
                currency={getCurrencyName()}
              />
            )}
          />
          <Controller
            name="unitCurrency"
            control={control}
            rules={{ required: errors.unitCurrency ? errors.unitCurrency.message : undefined }}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                name="unitCurrency"
                id="unitCurrency"
                value={String(watch('unitCurrency'))}
                label={f({ id: 'form.inputs.currency' })}
                introOption={f({ id: 'form.choose' })}
                options={props.currencies || []}
                required={unitCurrencyRequired}
                onChangeCallback={(value: string) => {
                  setValue('unitCurrency', value);
                  setCurrencyId(value);
                  trackEvent(['bluecoin form', 'change', 'Select currency']);
                }}
                error={errors.unitCurrency?.message}
                touched={true}
              />
            )}
          />
          {props.id && data && data.fromTs && (
            <CmdLabel
              id="startTs"
              title={f({ id: 'bluecoin.assignment.assignFrom' })}
              value={dayjs(data.fromTs).format('DD.MM.YYYY HH:mm')}
            />
          )}
          {props.id && data && data.toTs && (
            <CmdLabel
              id="endTs"
              title={f({ id: 'bluecoin.assignment.assignTo' })}
              value={dayjs(data.toTs).format('DD.MM.YYYY HH:mm')}
            />
          )}
        </>
      )}
      {showLoader && <CmdLoader inline={true} />}
    </CmdModal>
  );
}
